import { AHSeparator, Box, ListItem, SectionHeader, useSpaceSize } from "arkh";
import { FlatList, Platform } from "react-native";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useCallback, useMemo } from "react";

import { ColorSelectItem } from "./ColorSelect";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { TodoCategoryEditScreenDeleteMutation } from "./__generated__/TodoCategoryEditScreenDeleteMutation.graphql";
import { TodoCategoryEditScreenQuery } from "./__generated__/TodoCategoryEditScreenQuery.graphql";
import { useAlert } from "../../common/Alert";
import { StoreAdminNavigationScreenProps } from "../StoreAdmin/StoreAdminNavigation";
import { TodoCategoryEditScreenMergeMutation } from "./__generated__/TodoCategoryEditScreenMergeMutation.graphql";
import { MoreItem } from "../../common/MoreButton";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { EntityHeader } from "@/src/components/EntityHeader";
import { useNavigationHeader } from "@/src/common/navigationHeaderOptions";
import { ActionFooter } from "@/src/components/ActionFooter";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type TodoCategoryEditScreenProps =
  StoreAdminNavigationScreenProps<"TodoCategoryEdit">;

export function TodoCategoryEditScreen(props: TodoCategoryEditScreenProps) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerTodoCategoryEditScreen {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerTodoCategoryEditScreen({
  route: { params },
  navigation,
  fetchKey,
}: TodoCategoryEditScreenProps & { fetchKey: string }) {
  const data = useLazyLoadQuery<TodoCategoryEditScreenQuery>(
    graphql`
      query TodoCategoryEditScreenQuery($id: ID!) {
        store {
          defaultTodoCategory {
            id
          }
        }
        todoCategory(id: $id) {
          id
          title
          icon
          totalCount
          isDefault
          tags {
            id
            title
            color
          }
        }
      }
    `,
    {
      id: params.id,
    },
    {
      fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const [deleteTodoCategory] =
    useMutation<TodoCategoryEditScreenDeleteMutation>(graphql`
      mutation TodoCategoryEditScreenDeleteMutation($id: ID!) {
        todoCategory(id: $id) {
          delete {
            id @deleteRecord
          }
        }
      }
    `);

  const [mergeTodoCategory] = useMutation<TodoCategoryEditScreenMergeMutation>(
    graphql`
      mutation TodoCategoryEditScreenMergeMutation(
        $id: ID!
        $targetTodoCategoryId: ID!
      ) {
        todoCategory(id: $id) {
          merge(targetTodoCategoryId: $targetTodoCategoryId) {
            id
          }
        }
      }
    `
  );

  const onMergeTodoCategory = useCallback(() => {
    if (data.store.defaultTodoCategory) {
      mergeTodoCategory({
        variables: {
          id: params.id,
          targetTodoCategoryId: data.store.defaultTodoCategory.id,
        },
        onCompleted: navigation.goBack,
      });
    }
  }, [
    data.store.defaultTodoCategory,
    mergeTodoCategory,
    navigation.goBack,
    params.id,
  ]);

  const title = data.todoCategory?.title ?? "";
  const icon = data.todoCategory?.icon ?? "Check";

  const onDeleteTodoCategory = useCallback(() => {
    deleteTodoCategory({
      variables: {
        id: params.id,
      },
      onCompleted: navigation.goBack,
    });
  }, [deleteTodoCategory, navigation, params.id]);

  const totalCount = useMemo(
    () => data?.todoCategory?.totalCount ?? 0,
    [data?.todoCategory?.totalCount]
  );

  const onPressDelete = useDeleteTodoCategoryAlert({
    onDelete: onDeleteTodoCategory,
    title,
    totalCount,
  });
  const onPressMergeTodoCategory = useDeleteAndMergeTodoCategoryAlert({
    onMerge: onMergeTodoCategory,
    title,
    totalCount,
  });

  const tags = data?.todoCategory?.tags ?? [];
  const renderTagItem = useCallback(
    ({ item, index }: { item: (typeof tags)[number]; index: number }) => {
      return (
        <Box spaceHorizontal="medium">
          <ListItem
            border="border-primary"
            isFirst={index === 0}
            isLast={index === tags.length - 1}
            left={<ColorSelectItem color={item.color} selected={false} />}
            right={<ListItem.NavArrow />}
            onPress={() => {
              navigation.navigateDeprecated("TodoCategoryTagEdit", {
                categoryId: params.id,
                tag: item,
              });
            }}
          >
            <ListItem.Title>{item.title}</ListItem.Title>
          </ListItem>
        </Box>
      );
    },
    [navigation, params.id, tags.length]
  );

  const intl = useIntl();

  useNavigationHeader({
    type: "BACK",
    onBack: () => {
      navigation.goBack();
    },
    more: data.todoCategory?.isDefault
      ? undefined
      : [
          {
            label: intl.formatMessage({
              defaultMessage: `Delete list`,
              description:
                "TodoCategoryEditScreen delete todo category button title",
            }),
            onPress: () => {
              onPressDelete();
            },
            icon: "trash",
            color: "negative",
            type: "button",
          },
          ...(data.store?.defaultTodoCategory &&
          data.todoCategory?.totalCount > 0
            ? [
                {
                  label: intl.formatMessage({
                    defaultMessage: `Delete list and move content to To Do`,
                    description:
                      "TodoCategoryEditScreen merge todo category admin button title",
                  }),
                  onPress: () => {
                    onPressMergeTodoCategory();
                  },
                  icon: "trash",
                  color: "negative",
                } as MoreItem,
              ]
            : []),
        ],
  });

  const s = useSpaceSize("medium");
  const insets = useSafeAreaInsets();

  if (!data.todoCategory) return null;
  return (
    <>
      <FlatList
        style={{ flex: 1 }}
        renderItem={renderTagItem}
        data={tags}
        contentContainerStyle={{
          paddingBottom: s + insets.bottom,
        }}
        ItemSeparatorComponent={AHSeparator}
        ListHeaderComponent={
          <>
            <EntityHeader>
              <EntityHeader.TodoCategoryIcon icon={icon} />
              <EntityHeader.Title>{title}</EntityHeader.Title>

              <EntityHeader.Button
                onPress={() => {
                  navigation.navigateDeprecated("TodoCategoryEditGeneral", {
                    id: params.id,
                  });
                }}
              >
                <FormattedMessage
                  defaultMessage="Edit Name & Icon"
                  description="TodoCategoryEditScreen edit name and icon button title"
                />
              </EntityHeader.Button>
            </EntityHeader>
            {tags.length > 0 ? (
              <Box spaceTop="large" spaceHorizontal="medium">
                <SectionHeader>
                  <SectionHeader.Title>
                    <FormattedMessage
                      defaultMessage="Tags"
                      description="StoreAdminTodoCategoriesEdit: Tags Header"
                    />
                  </SectionHeader.Title>
                </SectionHeader>
              </Box>
            ) : null}
          </>
        }
      />
      <ActionFooter
        safeInsetBottom
        primary={
          <ActionFooter.Primary
            title={
              <FormattedMessage
                defaultMessage="Create new tag"
                description="TodoCategoryEditScreen add tag button title"
              />
            }
            icon="tag"
            onPress={() => {
              navigation.navigateDeprecated("TodoCategoryTagCreate", {
                categoryId: params.id,
              });
            }}
          />
        }
      />
    </>
  );
}

function useDeleteTodoCategoryAlert({
  onDelete,
  title,
  totalCount,
}: {
  onDelete: () => void;
  title: string;
  totalCount: number;
}) {
  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const Alert = useAlert();
  const alertTitle = intl.formatMessage(
    {
      defaultMessage: "Delete {todoCategoryName}",
      description: "TodoCategory delete alert title",
    },
    {
      todoCategoryName: title,
    }
  );
  const deleteOptionTitle = intl.formatMessage({
    defaultMessage: "Delete",
    description: "TodoCategory delete action sheet delete option",
  });

  return useCallback(() => {
    if (totalCount === 0) {
      onDelete();
      return;
    }

    if (Platform.OS === "ios") {
      Alert.prompt(
        deleteOptionTitle,
        intl.formatMessage(
          {
            defaultMessage:
              "Confirm that you want to delete {todoCategoryName}. {totalCount} items will be deleted. Enter {todoCategoryName} in the text input and press OK.",
            description: "onDeleteTodoCategory prompt input description",
          },
          {
            totalCount,
            todoCategoryName: title,
          }
        ),
        (text) => {
          if (text === title) {
            onDelete();
          } else {
            Alert.alert(
              intl.formatMessage(
                {
                  defaultMessage: "{todoCategoryTitle} not deleted",
                  description: "onDeleteTodoCategory error alert title",
                },
                {
                  todoCategoryTitle: title,
                }
              ),
              intl.formatMessage(
                {
                  defaultMessage: `The input did not match {todoCategoryTitle}`,
                  description: "onDeleteTodoCategory error alert description",
                },
                {
                  todoCategoryTitle: title,
                }
              )
            );
          }
        },
        "plain-text"
      );
    } else {
      Alert.alert(
        alertTitle,
        intl.formatMessage(
          {
            defaultMessage:
              "Confirm that you want to delete {todoCategoryName}. {totalCount} items will be deleted.",
            description: "TodoCategory delete alert description",
          },
          {
            totalCount,
            todoCategoryName: title,
          }
        ),
        [
          {
            text: deleteOptionTitle,
            style: "destructive",
            onPress: onDelete,
          },
          {
            text: intl_tokens.Cancel,
            style: "cancel",
            onPress: () => {},
          },
        ]
      );
    }
  }, [
    Alert,
    alertTitle,
    deleteOptionTitle,
    intl,
    intl_tokens.Cancel,
    onDelete,
    title,
    totalCount,
  ]);
}

function useDeleteAndMergeTodoCategoryAlert({
  onMerge,
  title,
  totalCount,
}: {
  onMerge: () => void;
  title: string;
  totalCount: number;
}) {
  const intl = useIntl();
  const intl_tokens = useIntlTokens();
  const Alert = useAlert();
  const alertTitle = intl.formatMessage(
    {
      defaultMessage: `Delete {todoCategoryName} and move content to To Do`,
      description: "TodoCategory merge alert title",
    },
    {
      todoCategoryName: title,
    }
  );
  const deleteOptionTitle = intl.formatMessage({
    defaultMessage: "Delete and move to To Do",
    description: "TodoCategory merge action sheet delete option",
  });

  return useCallback(() => {
    if (totalCount === 0) {
      onMerge();
      return;
    }

    if (Platform.OS === "ios") {
      Alert.prompt(
        deleteOptionTitle,
        intl.formatMessage(
          {
            defaultMessage:
              "Confirm that you want to delete {todoCategoryName} and move content to To Do. {totalCount} items will be moved to To Do. Enter {todoCategoryName} in the text input and press OK.",
            description: "onMergeTodoCategory prompt input description",
          },
          {
            totalCount,
            todoCategoryName: title,
          }
        ),
        (text) => {
          if (text === title) {
            onMerge();
          } else {
            Alert.alert(
              intl.formatMessage(
                {
                  defaultMessage: "{todoCategoryTitle} not deleted",
                  description: "onMergeTodoCategory error alert title",
                },
                {
                  todoCategoryTitle: title,
                }
              ),
              intl.formatMessage(
                {
                  defaultMessage: `The input did not match {todoCategoryTitle}`,
                  description: "onMergeTodoCategory error alert description",
                },
                {
                  todoCategoryTitle: title,
                }
              )
            );
          }
        },
        "plain-text"
      );
    } else {
      Alert.alert(
        alertTitle,
        intl.formatMessage(
          {
            defaultMessage: `Confirm that you want to delete {todoCategoryName} and move content to To Do. {totalCount} items will be moved to To Do`,
            description: "TodoCategory delete and merge alert description",
          },
          {
            totalCount,
            todoCategoryName: title,
          }
        ),
        [
          {
            text: deleteOptionTitle,
            style: "destructive",
            onPress: onMerge,
          },
          {
            text: intl_tokens.Cancel,
            style: "cancel",
            onPress: () => {},
          },
        ]
      );
    }
  }, [
    Alert,
    alertTitle,
    deleteOptionTitle,
    intl,
    intl_tokens.Cancel,
    onMerge,
    title,
    totalCount,
  ]);
}
